import type {
  ApplicationDetail,
  ApplicationFastenerLine,
} from "@/api/applications";
import type { SelectorProduct } from "@/api/filter";

// =============================================================================
// T-Series part A / part B split — exact port of the POC
// (`isTSeriesMachineId` L3215, `shouldSplitForTSeries` L3221, `buildSideApp`
// L3247, `deriveAppsForCompare` L3293).
//
// WHY: a T-Series press installs one fastener side per stroke, so POC models a
// part with both an A side and a B side as TWO applications (one per side). The
// moment a T-Series machine is in the comparison, POC re-derives the app list —
// each base app becomes "<app> (A)" + "<app> (B)" — and recomputes EVERY selected
// machine against the split list (not just the T-Series card). Splitting halves
// the fasteners-per-part each machine sees, so the per-part installation cost,
// utilisation and required-machines change for all cards. MVP previously always
// fed the single combined app (A+B fasteners per part), so its numbers diverged
// from POC whenever a T-Series machine + a two-sided part were both present.
//
// The split drives comparison KPI cards and allocation-unit actions (Configure /
// Optimize) through scoped wire IDs. Saved applications and application-list
// identity remain the base UUID; no synthetic ID is persisted (ADR-0025).
// =============================================================================

/** POC `isTSeriesMachineId` — id/name contains "T-SERIES" or "TSERIES". */
export function isTSeriesMachineId(id: string | null | undefined): boolean {
  const s = String(id ?? "")
    .trim()
    .toUpperCase();
  if (!s) return false;
  return s.includes("T-SERIES") || s.includes("TSERIES");
}

/** POC `shouldSplitForTSeries` — any selected product is a T-Series press. */
export function shouldSplitForTSeries(
  products: ReadonlyArray<SelectorProduct>,
): boolean {
  return products.some(
    (p) => isTSeriesMachineId(p.productId) || isTSeriesMachineId(p.name),
  );
}

/** POC `fastenerQtyParts(f)[side]` — qtyPerPart falls back to the A side. */
function sideQty(f: ApplicationFastenerLine, side: "A" | "B"): number {
  const qa = f.qtyPerPartA;
  const qb = f.qtyPerPartB;
  const q = f.qtyPerPart;
  let a = qa != null && Number.isFinite(qa) ? qa : NaN;
  let b = qb != null && Number.isFinite(qb) ? qb : NaN;
  if (!Number.isFinite(a) && q != null && Number.isFinite(q) && !Number.isFinite(b)) {
    a = q;
  }
  if (!Number.isFinite(a)) a = 0;
  if (!Number.isFinite(b)) b = 0;
  return side === "A" ? a : b;
}

/** POC `buildSideApp` — one side of a base app, with side-only fastener qtys. */
function buildSideApp(
  base: ApplicationDetail,
  side: "A" | "B",
  allowEmpty: boolean,
): ApplicationDetail | null {
  const fasteners: ApplicationFastenerLine[] = [];
  for (const f of base.fasteners) {
    const q = sideQty(f, side);
    if (!(q > 0) && !allowEmpty) continue;
    fasteners.push({
      fastenerId: f.fastenerId,
      brand: f.brand,
      type: f.type,
      qtyPerPart: q > 0 ? q : null,
      qtyPerPartA: side === "A" ? (q > 0 ? q : null) : null,
      qtyPerPartB: side === "B" ? (q > 0 ? q : null) : null,
    });
  }
  const hasSide = fasteners.some((f) => (f.qtyPerPart ?? 0) > 0);
  if (!hasSide && !allowEmpty) return null;

  const suffix = ` (${side})`;
  return {
    ...base,
    applicationId: `${base.applicationId}::${side}`,
    name: base.name ? base.name + suffix : base.name,
    applicationNumber: base.applicationNumber
      ? base.applicationNumber + suffix
      : base.applicationNumber,
    fasteners,
  };
}

export interface DerivedCompareApps {
  apps: ApplicationDetail[];
  splitEnabled: boolean;
}

const ALLOCATION_SIDE_SUFFIX = /::[AB]$/i;

/** Base persisted UUID for a comparison allocation-unit id. */
export function baseApplicationIdFromScoped(value: string): string {
  return value.replace(ALLOCATION_SIDE_SUFFIX, "");
}

/**
 * Preserve machine assignments when the comparison enters/leaves T-Series
 * split mode. Base assignments fan out to the derived sides. Side assignments
 * collapse back only when the assigned sides agree (or only one side exists).
 */
export function reconcileTSeriesAssignments(args: {
  assignments: Readonly<Record<string, string>>;
  baseApplicationIds: ReadonlyArray<string>;
  scopedApplicationIds: ReadonlyArray<string>;
  splitEnabled: boolean;
}): Record<string, string> {
  const next = { ...args.assignments };
  const validScopedIds = new Set(args.scopedApplicationIds);

  for (const baseId of args.baseApplicationIds) {
    const sideIds = [`${baseId}::A`, `${baseId}::B`];
    if (args.splitEnabled) {
      const baseAssignment = next[baseId];
      if (baseAssignment) {
        for (const sideId of sideIds) {
          if (validScopedIds.has(sideId) && !next[sideId]) {
            next[sideId] = baseAssignment;
          }
        }
      }
      delete next[baseId];
      for (const sideId of sideIds) {
        if (!validScopedIds.has(sideId)) delete next[sideId];
      }
      continue;
    }

    const sideAssignments = sideIds
      .map((sideId) => next[sideId])
      .filter((productId): productId is string => !!productId);
    const distinctAssignments = new Set(sideAssignments);
    if (distinctAssignments.size === 1) {
      next[baseId] = sideAssignments[0]!;
    } else if (distinctAssignments.size > 1) {
      delete next[baseId];
    }
    for (const sideId of sideIds) delete next[sideId];
  }

  const previousKeys = Object.keys(args.assignments);
  const nextKeys = Object.keys(next);
  const unchanged =
    previousKeys.length === nextKeys.length &&
    nextKeys.every((key) => args.assignments[key] === next[key]);
  return unchanged ? (args.assignments as Record<string, string>) : next;
}

/**
 * POC `deriveAppsForCompare` — when a T-Series press is selected, split every
 * base app into its A and B sides (apps with no qty on a side are dropped; an
 * app with neither side falls back to an empty A so the row still renders).
 * Otherwise the base apps pass through unchanged.
 */
export function deriveAppsForCompare(
  appDetails: ReadonlyArray<ApplicationDetail>,
  selectedProducts: ReadonlyArray<SelectorProduct>,
): DerivedCompareApps {
  const splitEnabled = shouldSplitForTSeries(selectedProducts);
  if (!splitEnabled) return { apps: [...appDetails], splitEnabled };

  const derived: ApplicationDetail[] = [];
  for (const app of appDetails) {
    const a = buildSideApp(app, "A", false);
    const b = buildSideApp(app, "B", false);
    if (a) derived.push(a);
    if (b) derived.push(b);
    if (!a && !b) {
      const fallback = buildSideApp(app, "A", true);
      if (fallback) derived.push(fallback);
    }
  }
  return { apps: derived, splitEnabled };
}
